import { Link, useParams, useLocation } from "@tanstack/react-router";
import { X } from "lucide-react";
import { useEffect, type ComponentType } from "react";
import type { ModuleManifest } from "@praesto/contracts";
import { useCore } from "../lib/core";

/**
 * Slide-in left drawer (80% width max 280px). Lists the activeModule's
 * sub-routes. Tap navigates + closes. Overlay tap closes.
 */
export function MobileSubNavDrawer({
  activeModule,
  open,
  onClose,
}: {
  activeModule: ModuleManifest | null;
  open: boolean;
  onClose: () => void;
}) {
  const core = useCore();
  const { tenantSlug } = useParams({ strict: false }) as {
    tenantSlug: string;
  };
  const location = useLocation();

  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  if (!activeModule) return null;
  const subs = (activeModule.subRoutes ?? []).filter(
    (s) => !s.requires || core.rbac.can(s.requires.action, s.requires.resource),
  );

  return (
    <div className={`fixed inset-0 z-40 ${open ? "" : "pointer-events-none"}`} aria-hidden={!open}>
      <div
        className={`absolute inset-0 bg-overlay transition-opacity duration-200 ${
          open ? "opacity-100" : "opacity-0"
        }`}
        onClick={onClose}
      />
      {/* Always-Night surface: surface-night redeclares the whole token family
          so borders, hover veils and muted ink resolve dark in BOTH themes. */}
      <aside
        className={`surface-night absolute top-0 bottom-0 left-0 w-[80%] max-w-[280px] bg-sidebar-bg text-sidebar-fg shadow-lg transform transition-transform duration-200 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="px-4 py-3 border-b border-border-default flex items-center justify-between">
          <h2 className="text-eyebrow">{core.i18n.t(`${activeModule.id}.title`)}</h2>
          <button
            onClick={onClose}
            className="tap-target flex items-center justify-center -mr-2 rounded-sm hover:bg-hover"
            aria-label={core.i18n.t("shell.action.close")}
          >
            <X size={18} />
          </button>
        </div>
        <nav className="py-2 px-2 space-y-1">
          {subs.map((s) => {
            const Icon = s.icon as ComponentType<{ size?: number }>;
            const to = `/${tenantSlug}/${activeModule.id}/${s.path}`;
            const active = location.pathname.startsWith(to);
            return (
              <Link
                key={s.path}
                to={to as never}
                onClick={onClose}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm ${
                  active
                    ? "bg-sidebar-active text-sidebar-fg"
                    : "text-sidebar-muted hover:bg-hover hover:text-sidebar-fg"
                }`}
              >
                <Icon size={18} />
                {core.i18n.t(s.labelKey)}
              </Link>
            );
          })}
        </nav>
      </aside>
    </div>
  );
}
